import { useMutation, useQuery, useQueryClient } from "react-query";
import http from "shared/http";

export type UserInviteBasePath = "admin" | "agencymanager";

export interface UserInviteRequest {
  firstname: string;
  lastname: string;
  email: string;
  note?: string | null;
  agencyCode: string;
  roleName?: string;
}

export interface PendingUserInvite {
  id: number;
  firstname: string;
  lastname: string;
  email: string;
  note: string | null;
  agencyCode: string;
  invitedByName: string | null;
  createdAt: string;
  expiresAt: string;
  isExpired: boolean;
}

const getPendingInvitesQueryKey = (basePath: UserInviteBasePath) =>
  basePath === "admin" ? "admin-pending-invites" : "agency-manager-pending-invites";

export const useCreateUserInviteMutation = (basePath: UserInviteBasePath) => {
  const queryClient = useQueryClient();
  const queryKey = getPendingInvitesQueryKey(basePath);

  return useMutation(
    (invite: UserInviteRequest) => http.postAsync(`${basePath}/invites`, invite),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey]);
      },
    },
  );
};

export const useSearchUserInvitesQuery = (
  basePath: UserInviteBasePath,
  enabled: boolean,
) => {
  const queryKey = getPendingInvitesQueryKey(basePath);

  return useQuery(
    [queryKey],
    async (): Promise<PendingUserInvite[]> => {
      const response = await http.fetchAsync(`${basePath}/invites/search`);
      return response ?? [];
    },
    {
      enabled,
      staleTime: 15 * 1000,
    },
  );
};

export const useCancelUserInviteMutation = (basePath: UserInviteBasePath) => {
  const queryClient = useQueryClient();
  const queryKey = getPendingInvitesQueryKey(basePath);

  return useMutation(
    (id: number) => http.deleteAsync(`${basePath}/invites`, id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey]);
      },
    },
  );
};

export const useResendUserInviteMutation = (basePath: UserInviteBasePath) => {
  const queryClient = useQueryClient();
  const queryKey = getPendingInvitesQueryKey(basePath);

  return useMutation(
    (id: number) => http.postAsync(`${basePath}/invites/${id}/resend`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([queryKey]);
      },
    },
  );
};
